/**
 * Idaho leftover Mackay/Arco (and similar provider-based RHCs) often arrive with
 * NPPES primary taxonomy Family Medicine / Addiction Medicine (207QA0401X).
 * CMS CCN / POS certifies Rural Health Clinic — that must win over physician taxonomy
 * for Facility Type display and NPI apply (Playwright RHC assert; PR 5340 overreach).
 */

export const RHC_CHECKOUT_DISPLAY_NAME = "Rural Health Clinic";
export const RHC_CHECKOUT_SLUG = "rural_health_clinic";
export const RHC_NUCC_TAXONOMY_CODE = "261QR1300X";

const PHYSICIAN_TYPE_RE =
  /family\s*medicine|addiction\s*medicine|internal\s*medicine|\bphysician\b/i;
const RHC_TYPE_RE = /rural\s*health|\brhc\b|261qr1300x/i;

/** CMS RHC facility quads (last 4 of 6-digit CCN), mirrors ccnValidation / Rules. */
function isRhcCcn(ccn: unknown): boolean {
  const s = String(ccn ?? "").trim();
  if (!/^\d{6}$/.test(s)) return false;
  const quad = Number(s.slice(-4));
  return (
    (quad >= 3400 && quad <= 3499) ||
    (quad >= 3800 && quad <= 3999) ||
    (quad >= 8500 && quad <= 8999)
  );
}

function _norm(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function _asRecord(source: unknown): Record<string, unknown> {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }
  return source as Record<string, unknown>;
}

function _facilityDetails(source: unknown): Record<string, unknown> {
  const row = _asRecord(source);
  const details = row.facility_details;
  if (details && typeof details === "object" && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  return {};
}

function _taxonomyFrom(source: Record<string, unknown>): {
  code: string;
  desc: string;
} {
  const details = _facilityDetails(source);
  const nested = source.primary_taxonomy;
  const nestedObj =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : {};
  const detailsNested = details.primary_taxonomy;
  const detailsNestedObj =
    detailsNested &&
    typeof detailsNested === "object" &&
    !Array.isArray(detailsNested)
      ? (detailsNested as Record<string, unknown>)
      : {};
  return {
    code: _norm(
      source.primary_taxonomy_code ??
        details.primary_taxonomy_code ??
        nestedObj.code ??
        detailsNestedObj.code,
    ),
    desc: _norm(
      source.primary_taxonomy_desc ??
        details.primary_taxonomy_desc ??
        nestedObj.description ??
        nestedObj.desc ??
        detailsNestedObj.description ??
        detailsNestedObj.desc,
    ),
  };
}

function _collectCcn(...sources: unknown[]): string {
  for (const source of sources) {
    const row = _asRecord(source);
    const details = _facilityDetails(row);
    for (const key of ["ccn", "ccn_number", "medicare_ccn", "cms_ccn"] as const) {
      const raw = row[key] ?? details[key];
      const s = String(raw ?? "").trim();
      if (isRhcCcn(s)) return s;
    }
  }
  return "";
}

export function isPhysicianSpecialtyFacilityType(type: unknown): boolean {
  const n = String(type ?? "").trim();
  if (!n) return false;
  if (RHC_TYPE_RE.test(n)) return false;
  return PHYSICIAN_TYPE_RE.test(n);
}

export function isPhysicianPrimaryTaxonomy(...sources: unknown[]): boolean {
  return sources.some((source) => {
    if (!source) return false;
    const tax = _taxonomyFrom(_asRecord(source));
    const code = tax.code.replace(/\s+/g, "");
    if (/^207|^208|^209|^204|^206/.test(code)) return true;
    return PHYSICIAN_TYPE_RE.test(tax.desc);
  });
}

/**
 * When CMS CCN is an RHC quad, or Rules already confirmed RHC/POS, force RHC
 * over a physician specialty facility_type / taxonomy fallback.
 */
export function resolveRhcFacilityTypeOverride(
  ...sources: unknown[]
): { display_name: string; slug: string } | null {
  const ccn = _collectCcn(...sources);
  const hasRhcCcn = Boolean(ccn);

  let alreadyRhc = false;
  let physicianTyped = false;
  for (const source of sources) {
    if (!source) continue;
    const row = _asRecord(source);
    const details = _facilityDetails(row);
    const typeText = [
      row.facility_type,
      row.facility_type_slug,
      details.facility_type,
      details.facility_type_slug,
      row.match_method,
      (row.facility_type_obj as { display_name?: string; type_slug?: string } | undefined)
        ?.display_name,
      (row.facility_type_obj as { type_slug?: string } | undefined)?.type_slug,
    ]
      .map((v) => String(v ?? ""))
      .join(" ");
    if (RHC_TYPE_RE.test(typeText) || _norm(row.facility_type_slug) === RHC_CHECKOUT_SLUG) {
      alreadyRhc = true;
    }
    if (isPhysicianSpecialtyFacilityType(typeText) || isPhysicianPrimaryTaxonomy(source)) {
      physicianTyped = true;
    }
    const matchMethod = _norm(
      row.match_method ??
        (row.facility_type_obj as { match_method?: string } | undefined)?.match_method,
    );
    if (matchMethod === "pos" || matchMethod === "cms_ccn") {
      if (RHC_TYPE_RE.test(typeText) || _norm(row.facility_type_slug) === RHC_CHECKOUT_SLUG) {
        return {
          display_name: RHC_CHECKOUT_DISPLAY_NAME,
          slug: RHC_CHECKOUT_SLUG,
        };
      }
    }
  }

  if (hasRhcCcn && (physicianTyped || !alreadyRhc)) {
    // CCN 133990 etc. wins even when stored type is Family Medicine / Addiction Medicine.
    if (physicianTyped || !alreadyRhc) {
      return {
        display_name: RHC_CHECKOUT_DISPLAY_NAME,
        slug: RHC_CHECKOUT_SLUG,
      };
    }
  }

  // Rules POS already set RHC but Hub fell back to taxonomy description as facility_type.
  if (alreadyRhc && physicianTyped) {
    return {
      display_name: RHC_CHECKOUT_DISPLAY_NAME,
      slug: RHC_CHECKOUT_SLUG,
    };
  }

  return null;
}

export function applyRhcLookupTypeOverride(input: {
  facility_type?: string | null;
  facility_type_id?: number | null;
  facility_type_slug?: string | null;
  primary_taxonomy?: {
    code?: string;
    description?: string;
    desc?: string;
  } | null;
  primary_taxonomy_code?: string | null;
  primary_taxonomy_desc?: string | null;
  site_name?: string | null;
  organization_name?: string | null;
  facility_name?: string | null;
  ccn?: string | null;
  ccn_number?: string | null;
  match_method?: string | null;
  facility_type_obj?: { id?: number | null; match_method?: string; display_name?: string; type_slug?: string } | null;
}): {
  facility_type: string;
  facility_type_id: number | undefined;
  facility_type_slug: string | undefined;
} {
  const existingType = String(input.facility_type ?? "").trim();
  const existingSlug =
    String(input.facility_type_slug ?? "").trim() || undefined;
  const existingId =
    typeof input.facility_type_id === "number" &&
    Number.isFinite(input.facility_type_id) &&
    input.facility_type_id > 0
      ? input.facility_type_id
      : undefined;
  const rhc = resolveRhcFacilityTypeOverride(input);
  if (!rhc) {
    return {
      facility_type: existingType,
      facility_type_id: existingId,
      facility_type_slug: existingSlug,
    };
  }
  // Keep Rules POS / catalog RHC ids (CI 33177: Mackay POS returns id 12).
  // Prefer facility_type_obj.id when obj is already RHC — do not keep a physician
  // catalog id merely because objSlug says RHC (CI 33332: wrong keep blocked stamp).
  const objSlug = _norm(input.facility_type_obj?.type_slug);
  const objName = String(input.facility_type_obj?.display_name ?? "");
  const objIsRhc =
    objSlug === RHC_CHECKOUT_SLUG || RHC_TYPE_RE.test(objName);
  const objId =
    typeof input.facility_type_obj?.id === "number" &&
    Number.isFinite(input.facility_type_obj.id) &&
    input.facility_type_obj.id > 0
      ? input.facility_type_obj.id
      : undefined;
  let facility_type_id: number | undefined;
  if (objIsRhc && objId != null) {
    facility_type_id = objId;
  } else if (
    existingId != null &&
    (RHC_TYPE_RE.test(existingType) ||
      _norm(existingSlug) === RHC_CHECKOUT_SLUG)
  ) {
    facility_type_id = existingId;
  } else {
    facility_type_id = undefined;
  }
  return {
    facility_type: rhc.display_name,
    facility_type_id,
    facility_type_slug: rhc.slug,
  };
}

/**
 * Build facility_type_obj for CMS/NPI lookup cards after RHC override.
 * When override clears facility_type_id (physician → RHC), do not leak the
 * physician catalog id through the spread — card click requires a real RHC id.
 */
export function mergeLookupFacilityTypeObj(
  typed: {
    facility_type: string;
    facility_type_id?: number;
    facility_type_slug?: string;
  },
  // Accept NPIFacilityType (no index signature) without call-site casts.
  sourceFacilityType: object | null | undefined,
): Record<string, unknown> | undefined {
  const source =
    sourceFacilityType != null &&
    typeof sourceFacilityType === "object" &&
    !Array.isArray(sourceFacilityType)
      ? (sourceFacilityType as Record<string, unknown>)
      : undefined;
  if (!typed.facility_type_slug) {
    return source;
  }
  const merged: Record<string, unknown> = {
    ...(source || {}),
    display_name: typed.facility_type,
    type_slug: typed.facility_type_slug,
  };
  if (typed.facility_type_id != null) {
    merged.id = typed.facility_type_id;
  } else if (_norm(typed.facility_type_slug) === RHC_CHECKOUT_SLUG) {
    delete merged.id;
  }
  return merged;
}

/** True when lookup card is RHC by slug or CMS RHC CCN (associable after catalog id resolve). */
export function isRhcLookupFacilityForAssociation(facility: {
  facility_type_id?: number | null;
  facility_type_slug?: string | null;
  facility_type_obj?: { id?: number | null; type_slug?: string | null } | null;
  ccn?: string | null;
  ccn_number?: string | null;
  facility_type?: string | null;
} | null | undefined): boolean {
  if (!facility) return false;
  const slug = _norm(
    facility.facility_type_slug ?? facility.facility_type_obj?.type_slug,
  );
  if (slug === RHC_CHECKOUT_SLUG) return true;
  if (RHC_TYPE_RE.test(String(facility.facility_type ?? ""))) return true;
  return Boolean(resolveRhcFacilityTypeOverride(facility));
}


/**
 * Stamp Rural Health Clinic catalog id onto a typed lookup row when override
 * cleared physician id (or POS omitted id). Used at map-time so card click is
 * sync like hospital cards (CI 33177: async click-time resolve + PW re-clicks).
 */
export function withRhcCatalogTypeId<T extends {
  facility_type: string;
  facility_type_id?: number;
  facility_type_slug?: string;
}>(
  typed: T,
  rhcCatalogId: number | undefined,
): T {
  if (
    rhcCatalogId &&
    rhcCatalogId > 0 &&
    _norm(typed.facility_type_slug) === RHC_CHECKOUT_SLUG &&
    typed.facility_type_id == null
  ) {
    return { ...typed, facility_type_id: rhcCatalogId };
  }
  return typed;
}

export function pickRhcFacilityTypeIdFromCatalog(
  facilityTypes: Array<{ id: number; type_slug: string; display_name: string }>,
): number | undefined {
  const rhc = facilityTypes.find(
    (row) =>
      _norm(row.type_slug) === RHC_CHECKOUT_SLUG ||
      /rural\s*health\s*clinic/i.test(row.display_name),
  );
  return rhc?.id && rhc.id > 0 ? rhc.id : undefined;
}
